import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Menu, X, Phone, Calendar, User, ClipboardList, Search,
  MapPin, ChevronRight, Ambulance,
} from "lucide-react";
import {
  HOSPITAL, NAV, HERO_SLIDES, QUICK, STATS, CENTERS, DEPARTMENTS,
  DOCTORS, STORIES, PRESS, CHECKUP_PACKAGES, TIME_SLOTS,
} from "./gimpoWooriData";
import "./GimpoWooriSite.css";

function Reveal({ children, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add("is-visible"); obs.disconnect(); } },
      { threshold: 0.1, rootMargin: "0px 0px -30px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`gwh-reveal ${className}`.trim()}>{children}</div>;
}

export default function GimpoWooriSite({ embed = false }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [slide, setSlide] = useState(0);
  const [modal, setModal] = useState(null);
  const [docQ, setDocQ] = useState("");
  const [form, setForm] = useState({
    name: "", phone: "", dept: "", date: "", time: "", agree: false,
  });

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);

  const scroll = (id) => {
    setMenuOpen(false);
    if (id.startsWith("tel:")) { window.location.href = id; return; }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = (e) => {
    e.preventDefault();
    setModal("상담 신청이 접수되었습니다. 통화 가능 시간 내에 연락드리겠습니다.");
    setForm({ name: "", phone: "", dept: "", date: "", time: "", agree: false });
  };

  const filteredDocs = DOCTORS.filter(
    (d) => !docQ || d.name.includes(docQ) || d.dept.includes(docQ),
  );

  const current = HERO_SLIDES[slide];

  return (
    <div className={`gwh-site${embed ? " gwh-site--embed" : ""}`} data-testid="gimpo-woori-site">
      <div className="gwh-emergency">
        <div className="gwh-emergency__inner">
          <span><Ambulance size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />응급의료센터 <strong>{HOSPITAL.emergency}</strong> · 24시간</span>
          <span>건강검진 <strong>{HOSPITAL.checkup}</strong></span>
        </div>
      </div>

      <div className="gwh-top">
        <div className="gwh-top__inner">
          <span>대표 {HOSPITAL.phone}</span>
          <span>간편상담 {HOSPITAL.counsel}</span>
          <span>{HOSPITAL.address}</span>
        </div>
      </div>

      <header className="gwh-header">
        <div className="gwh-header__inner">
          <a href="#gwh-top" className="gwh-brand" onClick={(e) => { e.preventDefault(); scroll("gwh-top"); }}>
            <span className="gwh-brand__mark">{HOSPITAL.short}</span>
            <span className="gwh-brand__text">
              <strong>{HOSPITAL.name}</strong>
              <span>{HOSPITAL.nameEn}</span>
            </span>
          </a>
          <nav className="gwh-nav" aria-label="주요 메뉴">
            {NAV.map((n) => (
              <a key={n.id} href={`#${n.id}`} onClick={(e) => { e.preventDefault(); scroll(n.id); }}>{n.label}</a>
            ))}
          </nav>
          <button type="button" className="gwh-header__cta" onClick={() => scroll("booking")}>간편 상담 예약</button>
          <button type="button" className="gwh-menu-btn" aria-label="메뉴" onClick={() => setMenuOpen((v) => !v)}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menuOpen && (
          <nav className="gwh-mobile-nav">
            {NAV.map((n) => (
              <a key={n.id} href={`#${n.id}`} onClick={(e) => { e.preventDefault(); scroll(n.id); }}>{n.label}</a>
            ))}
          </nav>
        )}
      </header>

      <section id="gwh-top" className="gwh-hero">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide}
            className="gwh-hero__slide is-active"
            style={{ background: current.tone }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="gwh-hero__content">
              <span className="gwh-hero__tag">{current.tag}</span>
              <h1>{current.title}</h1>
              <p className="gwh-hero__desc">{current.desc}</p>
              <div className="gwh-hero__actions">
                <button type="button" className="gwh-btn gwh-btn--white" onClick={() => scroll("booking")}>
                  <Calendar size={16} /> 간편 상담 예약
                </button>
                <button type="button" className="gwh-btn gwh-btn--ghost" onClick={() => scroll("doctors")}>
                  의료진 찾기
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
        <div className="gwh-hero__dots">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`gwh-hero__dot${i === slide ? " is-active" : ""}`}
              aria-label={`슬라이드 ${i + 1}`}
              onClick={() => setSlide(i)}
            />
          ))}
        </div>
      </section>

      <div className="gwh-quick">
        <div className="gwh-quick__inner">
          {QUICK.map((q) => (
            <button key={q.id} type="button" className="gwh-quick__item" onClick={() => scroll(q.target)}>
              <span className="gwh-quick__icon">
                {q.icon === "calendar" && <Calendar size={18} />}
                {q.icon === "user" && <User size={18} />}
                {q.icon === "clipboard" && <ClipboardList size={18} />}
                {q.icon === "phone" && <Phone size={18} />}
              </span>
              {q.label}
            </button>
          ))}
        </div>
      </div>

      <div className="gwh-stats">
        {STATS.map((s) => (
          <div key={s.label} className="gwh-stat">
            <strong>{s.value}</strong>
            <span>{s.label}</span>
          </div>
        ))}
      </div>

      <div className="gwh-press">
        <div className="gwh-press__track">
          {[...PRESS, ...PRESS].map((p, i) => <span key={`${p}-${i}`}>{p}</span>)}
        </div>
      </div>

      <section id="guide" className="gwh-section">
        <div className="gwh-wrap">
          <Reveal>
            <div className="gwh-head">
              <span className="gwh-head__eyebrow">Guide</span>
              <h2>진료 안내</h2>
              <p>{HOSPITAL.intro}</p>
            </div>
          </Reveal>
          <Reveal>
            <div className="gwh-depts">
              {DEPARTMENTS.map((d) => <span key={d} className="gwh-dept">{d}</span>)}
            </div>
          </Reveal>
        </div>
      </section>

      <section id="centers" className="gwh-section gwh-section--white">
        <div className="gwh-wrap">
          <Reveal>
            <div className="gwh-head">
              <span className="gwh-head__eyebrow">Centers</span>
              <h2>전문 진료센터</h2>
              <p>분야별 전문센터에서 통합적인 의료 서비스를 제공합니다.</p>
            </div>
          </Reveal>
          <div className="gwh-centers">
            {CENTERS.map((c, i) => (
              <Reveal key={c.name}>
                <article className="gwh-center" style={{ background: c.tone }}>
                  <span className="gwh-center__badge" style={{ background: c.accent }} />
                  <h3>{c.name}</h3>
                  <p>{c.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="doctors" className="gwh-section">
        <div className="gwh-wrap">
          <Reveal>
            <div className="gwh-head">
              <span className="gwh-head__eyebrow">Doctors</span>
              <h2>의료진 찾기</h2>
              <p>김포우리병원의 의료진이 여러분의 건강 지킴이가 되겠습니다.</p>
            </div>
          </Reveal>
          <div className="gwh-search">
            <Search size={18} color="#64748b" />
            <input
              type="search"
              placeholder="의료진명 또는 진료과 검색"
              value={docQ}
              onChange={(e) => setDocQ(e.target.value)}
            />
          </div>
          <div className="gwh-doctors">
            {filteredDocs.map((d) => (
              <Reveal key={d.name}>
                <article className="gwh-doctor">
                  <span className="gwh-doctor__avatar">{d.name[0]}</span>
                  <div>
                    <strong>{d.name} {d.title}</strong>
                    <span>{d.dept}</span>
                  </div>
                  <ChevronRight size={16} color="#94a3b8" style={{ marginLeft: "auto" }} />
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="checkup" className="gwh-section gwh-section--white">
        <div className="gwh-wrap">
          <Reveal>
            <div className="gwh-head">
              <span className="gwh-head__eyebrow">Health Checkup</span>
              <h2>건강증진센터</h2>
              <p>검진 전문 상담 {HOSPITAL.checkup} · 맞춤형 패키지 안내</p>
            </div>
          </Reveal>
          <div className="gwh-packages">
            {CHECKUP_PACKAGES.map((p) => (
              <Reveal key={p.name}>
                <article className="gwh-package">
                  <h3>{p.name}</h3>
                  <div className="gwh-package__price">{p.price}</div>
                  <ul>{p.items.map((i) => <li key={i}>{i}</li>)}</ul>
                  <button type="button" className="gwh-btn gwh-btn--white" style={{ color: "var(--gwh-teal-deep)", width: "100%" }} onClick={() => scroll("booking")}>
                    검진 상담 신청
                  </button>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="stories" className="gwh-section">
        <div className="gwh-wrap">
          <Reveal>
            <div className="gwh-head">
              <span className="gwh-head__eyebrow">GWH+ Stories</span>
              <h2>우리 이야기</h2>
              <p>병원 소식, 건강 칼럼, 닥터 Who 감성 스토리</p>
            </div>
          </Reveal>
          <div className="gwh-stories">
            {STORIES.map((s) => (
              <Reveal key={s.title}>
                <article className="gwh-story">
                  <span className="gwh-story__cat">{s.category}</span>
                  <h3>{s.title}</h3>
                  <p>{s.summary}</p>
                  <time>{s.date}</time>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="booking" className="gwh-section gwh-section--white">
        <div className="gwh-wrap">
          <div className="gwh-booking">
            <div className="gwh-booking__grid">
              <div>
                <h2 style={{ margin: "0 0 0.5rem", fontSize: "1.35rem" }}>간편 상담 예약</h2>
                <p style={{ margin: "0 0 1rem", opacity: 0.9, fontSize: "0.85rem", lineHeight: 1.65 }}>
                  상담 전화가 2회 이상 연결되지 않을 경우 예약 의뢰가 자동 취소됩니다.
                  통화 가능 시간: 10:00~17:00 (점심 12:00~13:00)
                </p>
                <p style={{ margin: 0, fontSize: "0.8rem", opacity: 0.8 }}>
                  <Phone size={14} style={{ verticalAlign: "middle" }} /> {HOSPITAL.counsel}
                </p>
              </div>
              <form className="gwh-form" onSubmit={submit}>
                <input required placeholder="이름" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input required placeholder="연락처" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <select value={form.dept} onChange={(e) => setForm({ ...form, dept: e.target.value })}>
                  <option value="">진료과 선택</option>
                  {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                <select value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}>
                  <option value="">희망 통화 시간</option>
                  {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <label>
                  <input type="checkbox" checked={form.agree} onChange={(e) => setForm({ ...form, agree: e.target.checked })} required />
                  개인정보 수집·이용에 동의합니다
                </label>
                <button type="submit" className="gwh-btn gwh-btn--white" style={{ color: "var(--gwh-teal-deep)" }}>상담 신청</button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section id="about" className="gwh-section">
        <div className="gwh-wrap">
          <Reveal>
            <div className="gwh-head">
              <span className="gwh-head__eyebrow">Location</span>
              <h2>오시는 길</h2>
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", color: "var(--gwh-muted)", fontSize: "0.9rem" }}>
              <MapPin size={20} color="var(--gwh-teal)" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <strong style={{ display: "block", color: "var(--gwh-teal-deep)", marginBottom: "0.25rem" }}>{HOSPITAL.address}</strong>
                <span>{HOSPITAL.hours}</span><br />
                <span>대표 {HOSPITAL.phone} · 응급 {HOSPITAL.emergency}</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="gwh-footer">
        <div className="gwh-footer__grid">
          <div>
            <strong>{HOSPITAL.name}</strong>
            <p>{HOSPITAL.slogan}</p>
            <p style={{ marginTop: "0.5rem" }}>{HOSPITAL.address}</p>
          </div>
          <div>
            <strong>연락처</strong>
            <p>대표 {HOSPITAL.phone}</p>
            <p>상담 {HOSPITAL.counsel}</p>
            <p>검진 {HOSPITAL.checkup}</p>
          </div>
          <div>
            <strong>바로가기</strong>
            <p><a href="#guide">진료안내</a></p>
            <p><a href="#checkup">건강검진</a></p>
            <p><a href="#booking">예약 문의</a></p>
          </div>
        </div>
        <div className="gwh-footer__copy">
          © 2026 {HOSPITAL.nameEn} · Portfolio Redesign by DSiteSolution · 원본 gwhospital.co.kr 기반 리디자인 데모
        </div>
      </footer>

      <div className="gwh-float">
        <button type="button" className="gwh-float__btn gwh-float__btn--primary" onClick={() => scroll("booking")}>상담 예약</button>
        <a href={`tel:${HOSPITAL.phone}`} className="gwh-float__btn gwh-float__btn--tel">전화 연결</a>
      </div>

      {modal && (
        <div className="gwh-modal-overlay" role="dialog" onClick={() => setModal(null)}>
          <div className="gwh-modal" onClick={(e) => e.stopPropagation()}>
            <h3>접수 완료</h3>
            <p>{modal}</p>
            <button type="button" className="gwh-btn gwh-btn--white" style={{ color: "var(--gwh-teal-deep)" }} onClick={() => setModal(null)}>확인</button>
          </div>
        </div>
      )}
    </div>
  );
}
